import { useTranslation } from "react-i18next";
import type { BudgetUnit } from "../../api/entitlementBudgets";
import { Tooltip } from "../shared/Tooltip";
import { formatWholeUsd, isCapexInherited } from "./budgetFormat";
import styles from "./Budgets.module.css";

/** A unit's spend for the current month against its budget, from the budget report. */
export interface BudgetSpend {
  spendUsd: number;
  overBudget: boolean;
}

/**
 * The Budget column's value for one row of the groups hierarchy: the budget, and the hard stop as an icon with a label. An implied
 * budget is the same figure in a much lighter colour, with the word "implied" for assistive technology only. A row without the hard
 * stop keeps the icon's space, so every figure ends at the same place. Renders nothing for a unit with no budget and no hard stop.
 */
export function BudgetCellContent({ unit }: { unit: BudgetUnit | undefined }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!unit) {
    return null;
  }
  const hasBudget = unit.budgetUsd !== null;
  if (!hasBudget && !unit.effectiveHardStop) {
    return null;
  }
  const implied = unit.explicitBudgetUsd === null && unit.budgetUsd !== null;
  return (
    <span className={styles.cellAmountLine}>
      {hasBudget && (
        <span className={implied ? styles.implied : undefined}>
          {formatWholeUsd(unit.budgetUsd ?? 0)}
          {implied && <span className="visually-hidden"> {t("budgets.implied")}</span>}
        </span>
      )}
      {unit.effectiveHardStop ? (
        <Tooltip label={t("budgets.hardStop.on")} align="end">
          <HardStopIcon />
        </Tooltip>
      ) : (
        <span className={styles.hardStopSpace} aria-hidden="true" />
      )}
    </span>
  );
}

/**
 * The Consumed budget column's value: this month's spend in regular weight (the selected row's cell is made bold by the page). An
 * overspend is bold and red with an up-arrow after it, and "Over budget" for assistive technology only; a row within its budget
 * keeps the arrow's space, so every figure ends at the same place. Renders nothing for a unit with no budget to consume, or when the
 * caller may not see spend (`spend` is null).
 */
export function ConsumedBudgetCellContent({ unit, spend }: { unit: BudgetUnit | undefined; spend: BudgetSpend | null }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (unit?.budgetUsd == null || !spend) {
    return null;
  }
  return (
    <span className={spend.overBudget ? `${styles.cellAmountLine} ${styles.overAmount}` : styles.cellAmountLine}>
      <span>{formatWholeUsd(spend.spendUsd)}</span>
      {spend.overBudget
        ? <><OverIcon /><span className="visually-hidden">{t("budgets.overBudget")}</span></>
        : <span className={styles.overIconSpace} aria-hidden="true" />}
    </span>
  );
}

/**
 * The Capex column's value: the word "Capex" for a unit whose spend is Capex. A mark inherited from an ancestor is the same word in a lighter
 * colour, with "inherited" for assistive technology only, as an implied budget is. Renders nothing for an Opex unit.
 */
export function CapexCellContent({ unit }: { unit: BudgetUnit | undefined }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!unit?.effectiveCapex) {
    return null;
  }
  const inherited = isCapexInherited(unit);
  return (
    <span className={inherited ? styles.implied : undefined}>
      {t("budgets.capex.label")}
      {inherited && <span className="visually-hidden"> {t("budgets.capex.inheritedShort")}</span>}
    </span>
  );
}

/** An upward triangle that marks an overspend; its meaning is given in text for assistive technology. */
function OverIcon(): React.JSX.Element {
  return (
    <svg className={styles.overIcon} aria-hidden="true" focusable="false" width="10" height="10" viewBox="0 0 10 10">
      <path d="M5 1 9.5 9h-9z" fill="currentColor" />
    </svg>
  );
}

/** The hard-stop mark: a red prohibition ring on a white disc, a black dollar sign inside it, and a red diagonal line across it. Its label is the accessible name, so it is not read by colour or shape alone. */
export function HardStopIcon({ label }: { label?: string }): React.JSX.Element {
  const { t } = useTranslation();
  const text = label ?? t("budgets.hardStop.on");
  return (
    <svg className={styles.hardStopIcon} role="img" aria-label={text} focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10.5" fill="#ffffff" stroke="currentColor" strokeWidth="2.5" />
      <text x="12" y="16.5" textAnchor="middle" fontSize="14" fontWeight="700" fill="#000000">$</text>
      <path d="M5 5l14 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
